import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wrench, EnvelopeSimple, CheckCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={34} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-5">Check your email</h1>
          <p className="text-muted-foreground text-sm mt-2">
            If an account exists for <strong>{email}</strong>, we've sent a password reset link. It's valid for 60 minutes.
          </p>
          <Link to="/login" className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm mt-6 hover:underline" data-testid="back-to-login">
            <ArrowLeft size={15} weight="bold" /> Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <h1 className="font-heading text-2xl font-bold tracking-tight">Forgot your password?</h1>
          <p className="text-muted-foreground text-sm">Enter your registered email and we'll send you a link to reset it.</p>
          <div>
            <Label className="label-caps">Email</Label>
            <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="rounded-sm mt-1.5" required />
          </div>
          <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full rounded-sm font-semibold gap-2">
            <EnvelopeSimple size={17} weight="bold" /> {loading ? "Sending…" : "Send reset link"}
          </Button>
          <p className="text-sm text-center">
            <Link to="/login" className="text-muted-foreground hover:text-foreground" data-testid="back-to-login">← Back to sign in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
            <Wrench size={20} weight="bold" className="text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-xl">Fixigo</span>
        </div>
        {children}
      </div>
    </div>
  );
}
